import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Video, Phone, Apple, Copy, Check, Loader2, ExternalLink } from 'lucide-react';
import api from '@/lib/api';

/**
 * Public FaceTime landing page — ``/ft/{slug}``.
 *
 * On Apple devices: auto-redirects to ``facetime:`` / ``facetime-audio:``
 *   roughly 600 ms after load so the user can read who they're calling.
 * Elsewhere: shows a friendly "FaceTime requires an Apple device" screen with
 *   a copy-link button + a tel: fallback.
 */
function isAppleDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isMac = /Macintosh|Mac OS X/i.test(ua);
  const isIPadOS = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return isIOS || isMac || isIPadOS;
}

export default function FaceTimePublicPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isApple, setIsApple] = useState(false);
  const [copied, setCopied] = useState(false);
  const [autoLaunched, setAutoLaunched] = useState(false);

  useEffect(() => { setIsApple(isAppleDevice()); }, []);

  useEffect(() => {
    if (!slug) return;
    api.getPublicFaceTimePage(slug)
      .then(r => setData(r.data))
      .catch(err => setError(err?.response?.status === 404 ? 'not-found' : 'error'))
      .finally(() => setLoading(false));
  }, [slug]);

  const link = data ? `${data.mode === 'audio' ? 'facetime-audio' : 'facetime'}:${data.facetime_id}` : '';

  // Auto-launch FaceTime on Apple devices, but only once and after a short
  // delay so the user has a moment to confirm who they're calling.
  useEffect(() => {
    if (!data || !isApple || autoLaunched) return;
    const t = setTimeout(() => {
      window.location.href = link;
      setAutoLaunched(true);
    }, 700);
    return () => clearTimeout(t);
  }, [data, isApple, autoLaunched, link]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#5856D6] via-[#4F46E5] to-[#3730A3]">
        <Loader2 className="w-6 h-6 animate-spin text-white" />
      </div>
    );
  }
  if (error === 'not-found') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md text-center" data-testid="facetime-not-found">
          <p className="text-7xl text-slate-300 font-bold">404</p>
          <h1 className="text-xl font-semibold text-slate-900 mt-3">This FaceTime link isn't active</h1>
          <p className="text-sm text-slate-600 mt-1">The link is either wrong or has been disabled by the owner.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <p className="text-sm text-red-600">Something went wrong — please try again later.</p>
      </div>
    );
  }

  const initials = (data.member_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const tel = data.facetime_id?.startsWith('+') || /^\d/.test(data.facetime_id || '') ? data.facetime_id : null;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://realleadscrm.com';
  const pageUrl = `${origin}/ft/${data.slug}`;
  const previewTitle = `FaceTime ${data.member_name}`;
  const fullTitle = `${previewTitle} · Real Leads CRM`;
  const previewDescription = data.greeting
    || `${data.member_name}${data.member_company ? ' • ' + data.member_company : ''} — tap to start a FaceTime ${data.mode || 'video'} call instantly.`;
  const previewImage = data.member_avatar_url || `${origin}/og-image.png`;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#5856D6] via-[#4F46E5] to-[#3730A3] flex items-center justify-center px-4 py-12"
      data-testid="facetime-public-page"
    >
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={previewDescription} />
        {/* Open Graph (Facebook, LinkedIn, Slack, iMessage LP) */}
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="Real Leads CRM" />
        <meta property="og:title" content={previewTitle} />
        <meta property="og:description" content={previewDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={previewImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`Avatar of ${data.member_name}`} />
        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={previewTitle} />
        <meta name="twitter:description" content={previewDescription} />
        <meta name="twitter:image" content={previewImage} />
        {/* Apple LinkPresentation hints — Safari uses these for iMessage previews */}
        <meta name="apple-mobile-web-app-title" content={previewTitle} />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="theme-color" content="#5856D6" />
        <link rel="canonical" href={pageUrl} />
      </Helmet>
      <div className="max-w-md w-full">
        <div className="bg-white rounded-md shadow-2xl overflow-hidden">
          <div className="px-6 sm:px-8 pt-8 sm:pt-10 pb-6 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#5856D6] to-[#3730A3] text-white text-2xl font-bold shadow-lg" data-testid="facetime-avatar">
              {data.member_avatar_url
                ? <img src={data.member_avatar_url} alt={data.member_name} className="w-full h-full rounded-full object-cover" />
                : initials}
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 mt-4" data-testid="facetime-member-name">{data.member_name}</h1>
            {data.member_company && <p className="text-sm text-slate-500 mt-0.5">{data.member_company}</p>}
            {data.greeting && (
              <p className="mt-4 text-sm text-slate-700 italic px-2" data-testid="facetime-greeting">"{data.greeting}"</p>
            )}
          </div>

          <div className="px-6 sm:px-8 pb-8">
            {isApple ? (
              <>
                <a
                  href={link}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#5856D6] to-[#3730A3] hover:from-[#4F46E5] hover:to-[#312E81] text-white font-semibold py-3.5 rounded-md transition-all shadow-md hover:shadow-lg"
                  data-testid="facetime-launch-btn"
                >
                  {data.mode === 'audio' ? <Phone className="w-5 h-5" /> : <Video className="w-5 h-5" />}
                  Start FaceTime {data.mode === 'audio' ? 'Audio' : 'Video'}
                </a>
                <p className="mt-3 text-center text-xs text-slate-500 inline-flex items-center justify-center gap-1 w-full">
                  <Loader2 className={`w-3 h-3 ${autoLaunched ? '' : 'animate-spin'}`} />
                  {autoLaunched ? 'Launched FaceTime' : 'Auto-launching FaceTime…'}
                </p>
              </>
            ) : (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 mb-4" data-testid="facetime-apple-required">
                  <div className="flex items-start gap-2">
                    <Apple className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-900">FaceTime requires an Apple device</p>
                      <p className="text-xs text-amber-800 mt-0.5">Open this link on an iPhone, iPad, or Mac to start the call.</p>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-medium py-3 rounded-md transition-colors"
                  data-testid="facetime-copy-link-btn"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied — paste on your iPhone' : 'Copy link'}
                </button>
                {tel && (
                  <a
                    href={`tel:${tel}`}
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium py-3 rounded-md transition-colors text-sm"
                    data-testid="facetime-tel-fallback"
                  >
                    <Phone className="w-4 h-4" /> Call {data.facetime_id} instead
                  </a>
                )}
              </>
            )}
          </div>
        </div>
        <p className="mt-6 text-center text-[11px] text-white/60">
          Powered by Real Leads CRM ·{' '}
          <a href="/" className="underline hover:text-white inline-flex items-center gap-1">
            realleadscrm.com <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </p>
      </div>
    </div>
  );
}
